import { PROFILES as MEASUREMENT_PROFILES, PROFILE_GROUPS } from './measurement-profiles';
import {
  getPressureTemplateProfiles,
  resolvePressureTemplateProfile,
  resolvePressureTemplateProfileByCode,
} from './pressure-pilot-profiles';
import { DIMENSIONAL_TEMPLATES } from './dimensional-calibration-profile';
import { ENVIRONMENTAL_TEMPLATES } from './environmental-calibration-profile';
import { ANALYTICAL_TEMPLATES } from './analytical-calibration-profile';

const FAMILY_MODEL = [
  {
    key: 'pressure',
    label: 'Presión',
    description: 'Patrones de presión, manómetros y presostatos.',
    subgroups: [
      {
        key: 'manometros',
        label: 'Manómetros',
        description: 'CPH 6000 y CPH 6600.',
      },
      {
        key: 'presostatos',
        label: 'Presostatos',
        description: 'Instrumentos con histéresis y punto de disparo.',
      },
    ],
  },
  {
    key: 'dimensional',
    label: 'Dimensional',
    description: 'Longitud, espesor y familias especiales.',
    subgroups: [
      {
        key: 'longitud',
        label: 'Longitud',
        description: 'Calibres, micrómetros y profundidad.',
      },
      {
        key: 'espesor',
        label: 'Espesor',
        description: 'Espesores, recubrimientos y ultrasonidos.',
      },
      {
        key: 'especial',
        label: 'Especial',
        description: 'Comparadores y soldadura.',
      },
    ],
  },
  {
    key: 'force',
    label: 'Fuerza',
    description: 'Carga, torque y dinamometría.',
    subgroups: [
      {
        key: 'fuerza',
        label: 'Fuerza',
        description: 'Dinamómetros y células de carga.',
      },
      {
        key: 'torque',
        label: 'Torque',
        description: 'Torquímetros.',
      },
    ],
  },
  {
    key: 'gas',
    label: 'Gases',
    description: 'Detectores portátiles y fijos por aplicación de gas patrón certificado.',
    subgroups: [
      { key: 'monogas', label: 'Monogás', description: 'Un único sensor o analito.' },
      { key: 'multigas', label: 'Multigás', description: 'Varios sensores en una misma sesión.' },
    ],
  },
  {
    key: 'valve',
    label: 'Válvulas',
    description: 'Válvulas antirretorno, de seguridad, presión y vacío.',
    subgroups: [
      { key: 'check_valve', label: 'Antirretorno', description: 'Paso directo y hermeticidad frente a flujo inverso.' },
      { key: 'safety_pressure', label: 'Seguridad a presión', description: 'Válvulas PSV y de alivio a presión positiva.' },
      { key: 'safety_vacuum', label: 'Seguridad a vacío', description: 'Válvulas de alivio o protección por vacío.' },
    ],
  },
  {
    key: 'temperature',
    label: 'Temperatura',
    description: 'Termómetros, sensores, pirómetros y cámaras térmicas.',
    subgroups: [
      {
        key: 'contacto',
        label: 'Contacto',
        description: 'Termómetros de contacto y de líquido en vidrio.',
      },
      {
        key: 'infrarroja',
        label: 'Infrarroja',
        description: 'Pirómetros y termómetros infrarrojos.',
      },
      {
        key: 'sensores',
        label: 'RTD y termocuplas',
        description: 'RTD, PT100, PT1000 y termocuplas.',
      },
      {
        key: 'camaras',
        label: 'Estufas y cámaras',
        description: 'Estufas, cámaras y recintos térmicos.',
      },
    ],
  },
  {
    key: 'electrical',
    label: 'Eléctrica',
    description: 'Multímetros y comprobadores eléctricos.',
    subgroups: [
      {
        key: 'multimeter',
        label: 'Multímetro',
        description: 'Voltaje, corriente, resistencia y frecuencia.',
      },
    ],
  },
  {
    key: 'flow',
    label: 'Caudal',
    description: 'Caudalímetros y totalizadores.',
    subgroups: [
      {
        key: 'mass',
        label: 'Caudal másico',
        description: 'Caudalímetros másicos Coriolis con patrón F-003.',
      },
      {
        key: 'electromagnetic',
        label: 'Electromagnéticos',
        description: 'Dos series de diez comparaciones y datos de instalación.',
      },
      {
        key: 'mechanical_totalizer',
        label: 'Mecánicos / totalizadores',
        description: 'Comparación de caudal y verificación de volumen acumulado por tiempo.',
      },
      {
        key: 'ultrasonic',
        label: 'Ultrasónicos',
        description: 'Dos series de diez comparaciones y configuración de transductores.',
      },
      {
        key: 'rotameter',
        label: 'Flotámetros',
        description: 'Seis puntos de escala para líquidos o gases en condiciones de referencia.',
      },
    ],
  },
  {
    key: 'mass',
    label: 'Masa',
    description: 'Balanzas y básculas por comparación directa con masas patrón.',
    subgroups: [
      {
        key: 'balance',
        label: 'Balanzas',
        description: 'Balanzas monoplato con repetibilidad y excentricidad.',
      },
    ],
  },
  {
    key: 'environmental',
    label: 'SSHH / Ambiental',
    description: 'Velocidad de aire, iluminancia y nivel sonoro.',
    subgroups: [
      { key: 'air_velocity', label: 'Anemómetros', description: 'Anemómetros y termoanemómetros.' },
      { key: 'illuminance', label: 'Luxómetros', description: 'Medición de iluminancia.' },
      { key: 'sound_level', label: 'Decibelímetros', description: 'Nivel de presión sonora.' },
    ],
  },
  {
    key: 'analytical',
    label: 'Analítica',
    description: 'pH, conductividad, turbidez y ensayos fisicoquímicos.',
    subgroups: [
      { key: 'ph', label: 'pH', description: 'pHmetros y electrodos.' },
      { key: 'conductivity', label: 'Conductividad', description: 'Conductivímetros y sondas.' },
      { key: 'turbidity', label: 'Turbidez', description: 'Turbidímetros con estándares por lote.' },
      { key: 'dissolved_oxygen', label: 'Oxígeno disuelto', description: 'Oxímetros y sondas de O2.' },
      { key: 'refractometry', label: 'Refractometría', description: 'Refractómetros en grados Brix.' },
      { key: 'alcohol', label: 'Alcoholimetría', description: 'Alcoholímetros.' },
      { key: 'density', label: 'Densidad / Baumé', description: 'Densímetros y mostímetros.' },
      { key: 'volumetric', label: 'Material volumétrico', description: 'Buretas, probetas y micropipetas.' },
    ],
  },
];

const FAMILY_ORDER = FAMILY_MODEL.map((family) => family.key);

const SUBGROUP_LABELS = {
  pressure: {
    manometros: 'Manómetros',
    presostatos: 'Presostatos',
  },
  dimensional: {
    longitud: 'Longitud',
    espesor: 'Espesor',
    especial: 'Especial',
  },
  force: {
    fuerza: 'Fuerza',
    torque: 'Torque',
  },
  gas: {
    monogas: 'Monogás',
    multigas: 'Multigás',
  },
  valve: {
    check_valve: 'Antirretorno',
    safety_pressure: 'Seguridad a presión',
    safety_vacuum: 'Seguridad a vacío',
  },
  electrical: {
    multimeter: 'Multímetro',
  },
  temperature: {
    contacto: 'Contacto',
    infrarroja: 'Infrarroja',
    sensores: 'RTD y termocuplas',
    camaras: 'Estufas y cámaras',
  },
  flow: {
    mass: 'Caudal másico',
    electromagnetic: 'Electromagnéticos',
    mechanical_totalizer: 'Mecánicos / totalizadores',
    ultrasonic: 'Ultrasónicos',
    rotameter: 'Flotámetros',
  },
  mass: {
    balance: 'Balanzas',
  },
  environmental: {
    air_velocity: 'Anemómetros',
    illuminance: 'Luxómetros',
    sound_level: 'Decibelímetros',
  },
  analytical: {
    ph: 'pH',
    conductivity: 'Conductividad',
    turbidity: 'Turbidez',
    dissolved_oxygen: 'Oxígeno disuelto',
    refractometry: 'Refractometría',
    alcohol: 'Alcoholimetría',
    density: 'Densidad / Baumé',
    volumetric: 'Material volumétrico',
  },
};

const FORCE_SUBGROUP_BY_PROFILE = {
  'FORCE:dynamometer:v1': 'fuerza',
  'FORCE:torque_wrench:v1': 'torque',
  'FORCE:load_cell:v1': 'fuerza',
};

const CATALOG = [];

function addCatalogProfile(profile) {
  CATALOG.push(profile);
  return profile;
}

function buildDimensionalProfile(template) {
  if (!template?.code) return null;
  const subgroup = template.subfamily || 'longitud';
  const subgroupLabel = SUBGROUP_LABELS.dimensional[subgroup] || subgroup;

  return addCatalogProfile({
    family: 'dimensional',
    familyLabel: 'Dimensional',
    subfamily: subgroup,
    subfamilyLabel: subgroupLabel,
    templateCode: template.code,
    templateRevision: template.revision,
    label: template.profileLabel,
    displayName: template.profileLabel,
    summary: `${template.title}, con bloques específicos, trazabilidad e incertidumbre.`,
    workflow: 'dimensional_comparison',
    defaultBlockType: 'dimensional_comparison',
    source: 'dimensional-calibration-profile',
    unit: template.unit,
    defaultSetpoints: [...template.defaultSetpoints],
    profileKey: template.profileKey,
  });
}

function buildEnvironmentalProfile(template) {
  if (!template?.code) return null;
  const subgroup = template.subfamily;
  return addCatalogProfile({
    family: 'environmental',
    familyLabel: 'SSHH / Ambiental',
    subfamily: subgroup,
    subfamilyLabel: SUBGROUP_LABELS.environmental[subgroup] || subgroup,
    templateCode: template.code,
    templateRevision: template.revision,
    label: template.profileLabel,
    displayName: template.profileLabel,
    summary: `${template.title}, con trazabilidad, condiciones de ensayo e incertidumbre.`,
    workflow: 'environmental_comparison',
    defaultBlockType: 'environmental_comparison',
    source: 'environmental-calibration-profile',
    unit: template.unit,
    defaultSetpoints: [...template.defaultSetpoints],
    profileKey: template.profileKey,
  });
}

function buildAnalyticalProfile(template) {
  if (!template?.code) return null;
  const subgroup = template.subfamily;
  return addCatalogProfile({
    family: 'analytical',
    familyLabel: 'Analítica',
    subfamily: subgroup,
    subfamilyLabel: SUBGROUP_LABELS.analytical[subgroup] || subgroup,
    templateCode: template.code,
    templateRevision: template.revision,
    label: template.profileLabel,
    displayName: template.profileLabel,
    summary: `${template.title}, con materiales por lote, trazabilidad e incertidumbre.`,
    workflow: 'analytical_comparison',
    defaultBlockType: 'analytical_comparison',
    source: 'analytical-calibration-profile',
    unit: template.unit,
    defaultSetpoints: [...template.defaultSetpoints],
    profileKey: template.profileKey,
  });
}

function buildForceProfile(profileId) {
  const profile = MEASUREMENT_PROFILES[profileId];
  if (!profile || profile.family !== 'force') return null;

  const subgroup = profile.subfamily || FORCE_SUBGROUP_BY_PROFILE[profileId] || 'fuerza';
  const subgroupLabel = SUBGROUP_LABELS.force[subgroup] || subgroup;

  return addCatalogProfile({
    family: 'force',
    familyLabel: 'Fuerza',
    subfamily: subgroup,
    subfamilyLabel: subgroupLabel,
    templateCode: profileId,
    templateRevision: 'v1',
    label: profile.label,
    displayName: profile.label,
    summary: `Módulo específico de Fuerza / Torque para ${profile.label.toLowerCase()}, con repeticiones, incertidumbre y trazabilidad.`,
    workflow: 'force_torque',
    defaultBlockType: 'force_comparison',
    source: 'measurement-profiles',
    unit: profile.unit?.base || 'N',
    defaultSetpoints:
      Array.isArray(profile.ranges) && profile.ranges[0] && typeof profile.suggestSetpoints === 'function'
        ? profile.suggestSetpoints({
            min: profile.ranges[0].min,
            max: profile.ranges[0].max,
          })
        : [],
  });
}

function buildPressureProfiles() {
  const profiles = getPressureTemplateProfiles().map((profile) => {
    const subfamily = profile.workflow === 'calibration_editor' ? 'presostatos' : 'manometros';
    const subfamilyLabel = SUBGROUP_LABELS.pressure[subfamily] || subfamily;

    return {
      ...profile,
      family: 'pressure',
      familyLabel: 'Presión',
      subfamily,
      subfamilyLabel,
      source: 'pressure-pilot-profiles',
    };
  });

  profiles.forEach((profile) => addCatalogProfile(profile));
}

function buildMeasurementProfiles() {
  const forceIds = PROFILE_GROUPS.force || [];

  DIMENSIONAL_TEMPLATES.forEach((template) => {
    buildDimensionalProfile(template);
  });
  ENVIRONMENTAL_TEMPLATES.forEach((template) => {
    buildEnvironmentalProfile(template);
  });
  ANALYTICAL_TEMPLATES.forEach((template) => {
    buildAnalyticalProfile(template);
  });
  forceIds.forEach((profileId) => {
    buildForceProfile(profileId);
  });
}

buildPressureProfiles();
buildMeasurementProfiles();
addCatalogProfile({
  family: 'temperature',
  familyLabel: 'Temperatura',
  subfamily: 'contacto',
  subfamilyLabel: 'Contacto',
  templateCode: 'TEMP-CONTACT:thermometer:v2',
  templateRevision: '2',
  label: 'Termómetro de contacto',
  displayName: 'Termómetro de contacto',
  summary: 'Dos series ascendentes y descendentes, seis puntos y cálculo de incertidumbre.',
  workflow: 'temperature_comparison',
  defaultBlockType: 'temperature_contact',
  source: 'temperature-calibration-profile',
  unit: '°C',
  defaultSetpoints: [],
});
addCatalogProfile({
  family: 'temperature', familyLabel: 'Temperatura', subfamily: 'infrarroja', subfamilyLabel: 'Infrarroja',
  templateCode: 'TEMP-IR:pyrometer:v1', templateRevision: '1', label: 'Termómetro infrarrojo / pirómetro', displayName: 'Termómetro infrarrojo / pirómetro',
  summary: 'Seis puntos, dos series ascendentes y descendentes, emisividad, distancia e incertidumbre radiométrica.',
  workflow: 'temperature_comparison', defaultBlockType: 'temperature_comparison', source: 'temperature-calibration-profile', unit: '°C', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'temperature', familyLabel: 'Temperatura', subfamily: 'sensores', subfamilyLabel: 'RTD y termocuplas',
  templateCode: 'TEMP-SENSOR:rtd_thermocouple:v1', templateRevision: '1', label: 'RTD / PT100 / termocupla', displayName: 'RTD / PT100 / termocupla',
  summary: 'Tipo de sensor, conexión, junta fría, seis puntos y cuatro comparaciones por punto.',
  workflow: 'temperature_comparison', defaultBlockType: 'temperature_comparison', source: 'temperature-calibration-profile', unit: '°C', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'temperature', familyLabel: 'Temperatura', subfamily: 'camaras', subfamilyLabel: 'Estufas y cámaras',
  templateCode: 'TEMP-CHAMBER:oven_chamber:v1', templateRevision: '1', label: 'Estufa / cámara de temperatura', displayName: 'Estufa / cámara de temperatura',
  summary: 'Seis puntos, condición de carga, estabilización, uniformidad, estabilidad e incertidumbre.',
  workflow: 'temperature_comparison', defaultBlockType: 'temperature_comparison', source: 'temperature-calibration-profile', unit: '°C', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'flow',
  familyLabel: 'Caudal',
  subfamily: 'mass',
  subfamilyLabel: 'Caudal másico',
  templateCode: 'FLOW-MASS:coriolis:v1',
  templateRevision: '2',
  label: 'Caudalímetro másico Coriolis',
  displayName: 'Caudalímetro másico Coriolis',
  summary: 'Tres puntos configurables, tres repeticiones y cálculo de incertidumbre con F-003.',
  workflow: 'flow_mass',
  defaultBlockType: 'mass_flow',
  source: 'flow-calibration-profile',
  unit: 'kg/min',
  defaultSetpoints: [],
});
addCatalogProfile({
  family: 'flow', familyLabel: 'Caudal', subfamily: 'electromagnetic', subfamilyLabel: 'Electromagnéticos',
  templateCode: 'FLOW-VOLUME:electromagnetic:v1', templateRevision: '1', label: 'Caudalímetro electromagnético', displayName: 'Caudalímetro electromagnético',
  summary: 'Dos series de diez lecturas, instalación en cañería, desviación e incertidumbre.',
  workflow: 'flow_comparison', defaultBlockType: 'volumetric_flow', source: 'flow-calibration-profile', unit: 'm³/h', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'flow', familyLabel: 'Caudal', subfamily: 'mechanical_totalizer', subfamilyLabel: 'Mecánicos / totalizadores',
  templateCode: 'FLOW-VOLUME:mechanical_totalizer:v1', templateRevision: '1', label: 'Caudalímetro mecánico / totalizador', displayName: 'Caudalímetro mecánico / totalizador',
  summary: 'Dos series de diez lecturas y veinte verificaciones de volumen acumulado dividido por tiempo.',
  workflow: 'flow_comparison', defaultBlockType: 'volumetric_flow', source: 'flow-calibration-profile', unit: 'm³/h', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'flow', familyLabel: 'Caudal', subfamily: 'ultrasonic', subfamilyLabel: 'Ultrasónicos',
  templateCode: 'FLOW-VOLUME:ultrasonic:v1', templateRevision: '1', label: 'Caudalímetro ultrasónico', displayName: 'Caudalímetro ultrasónico',
  summary: 'Dos series de diez lecturas, geometría de cañería, montaje de transductores e incertidumbre.',
  workflow: 'flow_comparison', defaultBlockType: 'volumetric_flow', source: 'flow-calibration-profile', unit: 'm³/h', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'flow', familyLabel: 'Caudal', subfamily: 'rotameter', subfamilyLabel: 'Flotámetros',
  templateCode: 'FLOW-VOLUME:rotameter:v1', templateRevision: '1', label: 'Flotámetro / rotámetro', displayName: 'Flotámetro / rotámetro',
  summary: 'Seis puntos de escala, fluido y condiciones de referencia para gases o líquidos.',
  workflow: 'flow_comparison', defaultBlockType: 'volumetric_flow', source: 'flow-calibration-profile', unit: 'm³/h', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'mass',
  familyLabel: 'Masa',
  subfamily: 'balance',
  subfamilyLabel: 'Balanzas',
  templateCode: 'MASS-BALANCE:monoplato:v1',
  templateRevision: '1',
  label: 'Balanza monoplato',
  displayName: 'Balanza monoplato',
  summary: 'Siete cargas, cinco repeticiones completas, cuatro posiciones de excentricidad y presupuesto de incertidumbre.',
  workflow: 'mass_balance',
  defaultBlockType: 'mass_balance',
  source: 'mass-calibration-profile',
  unit: 'kg',
  defaultSetpoints: [],
});
addCatalogProfile({
  family: 'gas', familyLabel: 'Gases', subfamily: 'monogas', subfamilyLabel: 'Monogás',
  templateCode: 'GAS:monogas:v1', templateRevision: '1', label: 'Detector monogás', displayName: 'Detector monogás',
  summary: 'Comparación directa contra gas patrón certificado, desviación, incertidumbre y prueba funcional.',
  workflow: 'gas_detector', defaultBlockType: 'gas_response', source: 'gas-calibration-profile', unit: 'ppm / %', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'gas', familyLabel: 'Gases', subfamily: 'multigas', subfamilyLabel: 'Multigás',
  templateCode: 'GAS:multigas:v1', templateRevision: '1', label: 'Detector multigás', displayName: 'Detector multigás',
  summary: 'Canales H2S, CO, O2, LEL y SO2 según la composición certificada del cilindro.',
  workflow: 'gas_detector', defaultBlockType: 'gas_response', source: 'gas-calibration-profile', unit: 'ppm / %', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'valve', familyLabel: 'Válvulas', subfamily: 'check_valve', subfamilyLabel: 'Antirretorno',
  templateCode: 'VALVE:check_nonreturn:v1', templateRevision: '1', label: 'Válvula antirretorno', displayName: 'Válvula antirretorno / check',
  summary: 'Paso directo, presión inversa corregida, hermeticidad, incertidumbre y regla de decisión.',
  workflow: 'check_valve', defaultBlockType: 'check_valve_functional', source: 'check-valve-calibration-profile', unit: 'bar', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'valve', familyLabel: 'Válvulas de Seguridad', subfamily: 'safety_pressure', subfamilyLabel: 'Seguridad a presión',
  templateCode: 'VALVE:safety_pressure:v1', templateRevision: '1', label: 'Válvula de seguridad a presión', displayName: 'Válvula PSV / alivio',
  summary: 'Tres aperturas y cierres, corrección del patrón, blowdown, hermeticidad e incertidumbre.',
  workflow: 'safety_valve', defaultBlockType: 'valve_performance', source: 'safety-valve-calibration-profile', unit: 'bar', defaultSetpoints: [],
});
addCatalogProfile({
  family: 'valve', familyLabel: 'Válvulas de Seguridad', subfamily: 'safety_vacuum', subfamilyLabel: 'Seguridad a vacío',
  templateCode: 'VALVE:safety_vacuum:v1', templateRevision: '1', label: 'Válvula de seguridad a vacío', displayName: 'Válvula de alivio de vacío',
  summary: 'Apertura por vacío con unidad explícita mmHg o mmH2O y trazabilidad al patrón de presión.',
  workflow: 'safety_valve', defaultBlockType: 'valve_performance', source: 'safety-valve-calibration-profile', unit: 'mmH2O', defaultSetpoints: [],
});

const CATALOG_BY_CODE = new Map(CATALOG.map((profile) => [profile.templateCode, profile]));

export function getCalibrationTemplateProfiles() {
  return CATALOG.map((profile) => ({ ...profile }));
}

export function resolveCalibrationTemplateProfileByCode(templateCode) {
  if (!templateCode) return null;
  return CATALOG_BY_CODE.get(String(templateCode)) || resolvePressureTemplateProfileByCode(templateCode) || null;
}

export function getCalibrationTemplateFamilies() {
  const families = FAMILY_MODEL.map((family) => ({
    key: family.key,
    label: family.label,
    description: family.description,
    subgroups: family.subgroups.map((subgroup) => ({
      key: subgroup.key,
      label: subgroup.label,
      description: subgroup.description,
      profiles: [],
    })),
  }));

  const familyMap = new Map(families.map((family) => [family.key, family]));

  for (const profile of CATALOG) {
    const family = familyMap.get(profile.family);
    if (!family) continue;

    const subgroupKey = profile.subfamily || 'general';
    let subgroup = family.subgroups.find((item) => item.key === subgroupKey);

    if (!subgroup) {
      subgroup = {
        key: subgroupKey,
        label: profile.subfamilyLabel || subgroupKey,
        description: '',
        profiles: [],
      };
      family.subgroups.push(subgroup);
    }

    subgroup.profiles.push(profile);
  }

  return families.filter((family) => family.subgroups.some((subgroup) => subgroup.profiles.length > 0 || family.key === 'dimensional'));
}

export function getTemplateCategories() {
  return getCalibrationTemplateFamilies();
}

function matchesType(type, ...needles) {
  const text = String(type || '').toLowerCase();
  return needles.some((needle) => text.includes(String(needle).toLowerCase()));
}

function isForceInstrument(instrument = {}) {
  const unit = String(instrument.unit || '').toLowerCase();
  return (
    matchesType(
      instrument.type,
      'force', 'fuerza', 'torque', 'torquímetro', 'torquimetro',
      'load cell', 'célula de carga', 'celula de carga',
      'dynamometer', 'dinamómetro', 'dinamometro', 'tensiómetro', 'tensiometro',
    ) ||
    ['n', 'kn', 'kgf', 'gf', 'lbf', 'nm', 'n·m', 'n.m', 'kgf.cm', 'kgf·cm', 'kgf.m', 'kgf·m', 'lbf·ft', 'lb-ft', 'lbf·in', 'lb-in'].includes(unit)
  );
}

function isGasInstrument(instrument = {}) {
  const unit = String(instrument.unit || '').toLowerCase();
  return matchesType(instrument.type, 'detector de gas', 'detector gas', 'multigas', 'monogas', 'gas detector', 'h2s', 'co2', 'oxígeno', 'oxigeno', 'lel', 'so2', 'nh3', 'hcn')
    || ['ppm', '%vol', '% vol', '%lel', '% lel'].includes(unit);
}

function isSafetyValveInstrument(instrument = {}) {
  return matchesType(instrument.type, 'válvula de seguridad', 'valvula de seguridad', 'safety valve', 'relief valve', 'psv');
}

function isCheckValveInstrument(instrument = {}) {
  return matchesType(instrument.type, 'válvula anti retorno', 'valvula anti retorno', 'válvula antirretorno', 'valvula antirretorno', 'check valve', 'non-return valve', 'non return valve');
}

function isFlowInstrument(instrument = {}) {
  const unit = String(instrument.unit || '').toLowerCase();
  return (
    matchesType(instrument.type, 'caudal', 'flowmeter', 'coriolis', 'rotam', 'flotam', 'totaliz') ||
    ['kg/min', 'kg/h', 't/h', 'g/min', 'm3/h', 'm³/h', 'l/min', 'l/h', 'l/s', 'nl/h'].includes(unit)
  );
}

function isMassInstrument(instrument = {}) {
  const type = String(instrument.type || '').toLowerCase();
  const unit = String(instrument.unit || '').toLowerCase();
  return (
    matchesType(type, 'balanza', 'báscula', 'bascula', 'weighing scale', 'weigh scale', 'balance', 'masa patrón', 'masa patron', 'pesa patrón', 'pesa patron', 'test weight')
    || (['kg', 'g', 'gr', 'mg', 't', 'tn', 'lb', 'lbs'].includes(unit) && !matchesType(type, 'caudal', 'flow', 'coriolis', 'másic', 'masic'))
  );
}

function isMassWeightInstrument(instrument = {}) {
  return matchesType(instrument.type, 'masa patrón', 'masa patron', 'pesa patrón', 'pesa patron', 'peso patrón', 'peso patron', 'test weight', 'standard weight', 'pesa calibrada', 'juego de pesas');
}

function isDimensionalInstrument(instrument = {}) {
  const unit = String(instrument.unit || '').toLowerCase();
  return (
    matchesType(instrument.type, 'dimensional', 'calibre', 'caliper', 'pie de rey', 'micrómetro', 'micrometro', 'rule', 'regla', 'flexómetro', 'flexometro', 'profundidad', 'depth', 'gauge', 'espesor', 'thick', 'ultrason', 'ultrasón', 'revest', 'coating', 'rugos', 'comparador', 'soldadura', 'peine', 'rosca', 'tamiz', 'probeta', 'pieza patrón', 'pieza patron', 'goniómetro', 'goniometro', 'nivel digital') ||
    ['mm', 'cm', 'm', 'um', 'µm', 'in', '°', 'deg'].includes(unit)
  );
}

function isEnvironmentalInstrument(instrument = {}) {
  const unit = String(instrument.unit || '').toLowerCase();
  return matchesType(instrument.type, 'anemómetro', 'anemometro', 'termoanemómetro', 'termoanemometro', 'velocidad de aire', 'luxómetro', 'luxometro', 'iluminancia', 'decibelímetro', 'decibelimetro', 'sonómetro', 'sonometro', 'sound level meter')
    || ['m/s', 'm/seg', 'km/h', 'ft/min', 'fpm', 'lx', 'lux', 'klx', 'db', 'dba', 'dbc'].includes(unit);
}

function isAnalyticalInstrument(instrument = {}) {
  return matchesType(
    instrument.type,
    'phmetro', 'ph metro', 'medidor de ph', 'conductiv', 'turbid',
    'oxímetro', 'oximetro', 'oxígeno disuelto', 'oxigeno disuelto',
    'refract', 'brix', 'alcoholímetro', 'alcoholimetro',
    'densímetro', 'densimetro', 'mostímetro', 'mostimetro', 'baumé', 'baume',
    'bureta', 'micropipeta', 'pipeta', 'material volumétrico', 'material volumetrico',
  );
}

export function resolveCalibrationTemplateProfile({ instrument } = {}) {
  if (!instrument) return null;

  const type = String(instrument.type || '').toLowerCase();
  const unit = String(instrument.unit || '').toLowerCase();

  if (isAnalyticalInstrument(instrument)) {
    if (matchesType(type, 'conductiv')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-CONDUCTIVITY:conductivity_meter:v1');
    if (matchesType(type, 'turbid')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-TURBIDITY:turbidimeter:v1');
    if (matchesType(type, 'oxímetro', 'oximetro', 'oxígeno disuelto', 'oxigeno disuelto')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-DO:oximeter:v1');
    if (matchesType(type, 'refract', 'brix')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-BRIX:refractometer:v1');
    if (matchesType(type, 'alcohol')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-ALCOHOL:alcohol_meter:v1');
    if (matchesType(type, 'mostímetro', 'mostimetro', 'baumé', 'baume')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-BAUME:must_meter:v1');
    if (matchesType(type, 'densímetro', 'densimetro')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-DENSITY:density_meter:v1');
    if (matchesType(type, 'bureta', 'micropipeta', 'pipeta', 'material volumétrico', 'material volumetrico')) return resolveCalibrationTemplateProfileByCode('ANALYTICAL-VOLUME:volumetric:v1');
    return resolveCalibrationTemplateProfileByCode('ANALYTICAL-PH:ph_meter:v1');
  }

  if (isEnvironmentalInstrument(instrument)) {
    if (matchesType(type, 'lux', 'iluminancia')) return resolveCalibrationTemplateProfileByCode('ENV-LIGHT:luxmeter:v1');
    if (matchesType(type, 'decibel', 'sonómetro', 'sonometro', 'sound level') || ['db', 'dba', 'dbc'].includes(unit)) return resolveCalibrationTemplateProfileByCode('ENV-SOUND:sound_level_meter:v1');
    return resolveCalibrationTemplateProfileByCode('ENV-AIR:anemometer:v1');
  }

  if (isCheckValveInstrument(instrument)) {
    return resolveCalibrationTemplateProfileByCode('VALVE:check_nonreturn:v1');
  }

  if (isSafetyValveInstrument(instrument)) {
    const vacuum = matchesType(type, 'vacío', 'vacio', 'vacuum') || ['mmhg', 'mmh2o', 'mmh₂o'].includes(unit);
    return resolveCalibrationTemplateProfileByCode(vacuum ? 'VALVE:safety_vacuum:v1' : 'VALVE:safety_pressure:v1');
  }

  if (isMassInstrument(instrument)) {
    if (isMassWeightInstrument(instrument)) return null;
    return resolveCalibrationTemplateProfileByCode('MASS-BALANCE:monoplato:v1');
  }

  if (
    matchesType(type, 'temperat', 'termometr', 'termómetr', 'thermomet', 'pirómetro', 'pirometro', 'rtd', 'pt100', 'termocupla', 'estufa') ||
    ['°c', 'ºc', 'c', '°f', 'ºf', 'f', 'k'].includes(unit)
  ) {
    if (matchesType(type, 'pirómetro', 'pirometro', 'pyrometer', 'infrarro')) {
      return resolveCalibrationTemplateProfileByCode('TEMP-IR:pyrometer:v1');
    }
    if (matchesType(type, 'rtd', 'pt100', 'pt1000', 'termocupla', 'thermocouple')) {
      return resolveCalibrationTemplateProfileByCode('TEMP-SENSOR:rtd_thermocouple:v1');
    }
    if (matchesType(type, 'estufa', 'cámara térmica', 'camara termica', 'cámara climática', 'camara climatica', 'incubadora', 'horno')) {
      return resolveCalibrationTemplateProfileByCode('TEMP-CHAMBER:oven_chamber:v1');
    }
    return resolveCalibrationTemplateProfileByCode('TEMP-CONTACT:thermometer:v2');
  }

  if (
    matchesType(type, 'presost', 'pressure switch', 'switch', 'manometer', 'pressure') ||
    ['bar', 'psi', 'pa', 'kpa', 'mpa', 'mbar'].includes(unit)
  ) {
    return resolvePressureTemplateProfile({
      instrumentLrl: instrument.range_min ?? null,
      instrumentUrl: instrument.range_max ?? null,
    });
  }

  if (isFlowInstrument(instrument)) {
    if (
      matchesType(type, 'coriolis', 'masic', 'másic', 'mass flow') ||
      ['kg/min', 'kg/h', 't/h', 'g/min'].includes(unit)
    ) {
      return resolveCalibrationTemplateProfileByCode('FLOW-MASS:coriolis:v1');
    }
    if (matchesType(type, 'electromagn', 'magnetic flow')) {
      return resolveCalibrationTemplateProfileByCode('FLOW-VOLUME:electromagnetic:v1');
    }
    if (matchesType(type, 'mecánico', 'mecanico', 'totaliz', 'turbina', 'engranaje')) {
      return resolveCalibrationTemplateProfileByCode('FLOW-VOLUME:mechanical_totalizer:v1');
    }
    if (matchesType(type, 'ultrasónico', 'ultrasonico', 'ultrasonic', 'clamp-on', 'clamp on')) {
      return resolveCalibrationTemplateProfileByCode('FLOW-VOLUME:ultrasonic:v1');
    }
    if (matchesType(type, 'rotam', 'flotam')) {
      return resolveCalibrationTemplateProfileByCode('FLOW-VOLUME:rotameter:v1');
    }
    return resolveCalibrationTemplateProfileByCode('FLOW-VOLUME:electromagnetic:v1');
  }

  if (isGasInstrument(instrument)) {
    return resolveCalibrationTemplateProfileByCode(matchesType(type, 'multi') ? 'GAS:multigas:v1' : 'GAS:monogas:v1');
  }

  if (isDimensionalInstrument(instrument)) {
    if (matchesType(type, 'microm', 'micróm')) return resolveCalibrationTemplateProfileByCode('DIM-LONG:micrometer:v1');
    if (matchesType(type, 'depth', 'profund')) return resolveCalibrationTemplateProfileByCode('DIM-LONG:depth:v1');
    if (matchesType(type, 'rule', 'regla', 'flex')) return resolveCalibrationTemplateProfileByCode('DIM-LONG:rule:v1');
    if (matchesType(type, 'ultrason', 'ultrasón') && matchesType(type, 'angular')) return resolveCalibrationTemplateProfileByCode('DIM-THICK:ultrasonic_angular:v1');
    if (matchesType(type, 'ultrason', 'ultrasón', 'medidor de espesores')) return resolveCalibrationTemplateProfileByCode('DIM-THICK:ultrasonic:v1');
    if (matchesType(type, 'revest', 'coating', 'rugos')) return resolveCalibrationTemplateProfileByCode('DIM-THICK:coating:v1');
    if (matchesType(type, 'soldadura')) {
      if (matchesType(type, 'angular', 'ángulo', 'angulo', 'angle', 'bisel')) return resolveCalibrationTemplateProfileByCode('DIM-ANGLE:welding_gauge:v1');
      return resolveCalibrationTemplateProfileByCode('DIM-SPECIAL:welding_gauge:v1');
    }
    if (matchesType(type, 'comparador')) return resolveCalibrationTemplateProfileByCode('DIM-SPECIAL:comparator:v1');
    if (matchesType(type, 'tres contactos', '3 contactos')) return resolveCalibrationTemplateProfileByCode('DIM-SPECIAL:three_contact:v1');
    if (matchesType(type, 'peine', 'rosca')) return resolveCalibrationTemplateProfileByCode('DIM-SPECIAL:thread_gauge:v1');
    if (matchesType(type, 'tamiz')) return resolveCalibrationTemplateProfileByCode('DIM-SPECIAL:sieve:v1');
    if (matchesType(type, 'probeta', 'pieza patrón', 'pieza patron')) return resolveCalibrationTemplateProfileByCode('DIM-SPECIAL:reference_piece:v1');
    if (matchesType(type, 'goniómetro', 'goniometro', 'nivel digital')) return resolveCalibrationTemplateProfileByCode('DIM-ANGLE:goniometer_level:v1');
    return resolveCalibrationTemplateProfileByCode('DIM-LONG:caliper:v1');
  }

  if (isForceInstrument(instrument)) {
    if (
      matchesType(type, 'torque', 'torquímetro', 'torquimetro')
      || ['nm', 'n·m', 'n.m', 'kgf.cm', 'kgf·cm', 'kgf.m', 'kgf·m', 'lbf·ft', 'lb-ft', 'lbf·in', 'lb-in'].includes(unit)
    ) return resolveCalibrationTemplateProfileByCode('FORCE:torque_wrench:v1');
    if (matchesType(type, 'load cell', 'célula de carga', 'celula de carga')) return resolveCalibrationTemplateProfileByCode('FORCE:load_cell:v1');
    return resolveCalibrationTemplateProfileByCode('FORCE:dynamometer:v1');
  }

  return resolvePressureTemplateProfile({
    instrumentLrl: instrument.range_min ?? null,
    instrumentUrl: instrument.range_max ?? null,
  });
}
